import {
  createUpdateNodeAction,
  FShallowNodeWithSelector,
  GraphActionType,
} from '@spazious/dollhouse-shallow-graph';

import { Commander, CommanderActionPhasesEnum, CommanderActionTypesEnum } from '../types.js';

import { BaseAction } from './BaseAction.js';
import { ActionSideEffect, ActionSideEffectTypesEnum } from './types.js';

/**
 * Base Action class
 */
export abstract class GraphNodeAction<
  Type extends CommanderActionTypesEnum,
  TPayload
> extends BaseAction<Type, TPayload> {
  private _selector: string;
  private _affectedNodes: FShallowNodeWithSelector[] = [];

  constructor(commander: Commander, type: Type, selector: string, payload?: TPayload | null) {
    super(commander, type, payload ?? null);
    this._selector = selector;
  }

  selector() {
    return this._selector;
  }

  _onAfterApply() {
    this.updateAffectedNodes();
  }

  _onBeforeCommit(): void {
    const { sideEffectHandlers } = this.commander;
    if (sideEffectHandlers?.onBeforeCommit) {
      this.applySideEffects(sideEffectHandlers.onBeforeCommit(this.commander.store.getState()));
    }
  }

  applySideEffects(sideEffects: ActionSideEffect[]) {
    const { staging } = this.commander;
    sideEffects.forEach((effect) => {
      if (effect.type === ActionSideEffectTypesEnum.Stacking) {
        staging.add(createUpdateNodeAction(effect.selector, 'parent', effect.parent));
        staging.add(createUpdateNodeAction(effect.selector, 'positionY', effect.positionY));
      }
    });
  }

  affectedNodes() {
    if (this.phase === CommanderActionPhasesEnum.Start) {
      console.warn('Action is not updated or commited yet');
    }
    return this._affectedNodes;
  }

  updateAffectedNodes() {
    const { prevState, staging } = this.commander;
    const nodes = this.commander.store.getState().nodes;
    this._affectedNodes = staging.stack.reduce((acc, { type, payload }) => {
      switch (type) {
        case GraphActionType.Add: {
          return [...acc, { ...nodes[payload.selector], selector: payload.selector }];
        }
        case GraphActionType.Update:
        case GraphActionType.Remove: {
          return [...acc, { ...prevState.nodes[payload.selector], selector: payload.selector }];
        }
      }
      return acc;
    }, [] as FShallowNodeWithSelector[]);
  }

  /**
   *
   */
  toJSON() {
    return {
      ...super.toJSON(),
      selector: this._selector,
    };
  }

  fromJSON(json: { type: Type; selector: string; payload: TPayload }) {
    this._selector = json.selector;

    return super.fromJSON(json);
  }
}
